import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { getAlbumLayout, IImageSize } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

type TAlbumLayoutItem = IImageSize;
type TAlbumLayoutRow = {
  id: number;
  items: TAlbumLayoutItem[];
};
type TChatMessagePayloads = ChatMessage['payload']['payloads'];
type TAlbumPayload = TChatMessagePayloads[0];

interface IAlbumPresenter {
  isFromMe: boolean;
  payloads: TChatMessagePayloads;
  groupId: string;
  messageId: string;
  isFakeGroup: boolean;
  layout: TAlbumLayoutRow[];

  getPayload(id: number): TAlbumPayload;
  setIsFromMe(isFromMe: boolean): void;
  setGroupId(groupId: string): void;
  setMessageId(messageId: string): void;
  setIsFakeGroup(isFakeGroup: boolean): void;
  setPayloads(payloads: TChatMessagePayloads): void;
  openMediaGallery(mediaId: string): void;
}

interface IAlbumPresenterProps {
  message: ChatMessage;
}

export function useAlbumPresenter({ message }: IAlbumPresenterProps): IAlbumPresenter {
  const presenter = useLocalObservable(() => ({
    isFromMe: message.fromMe && !message.group?.isChannel,
    payloads: message.payload.payloads,
    groupId: message.groupId,
    messageId: message.id,
    isFakeGroup: !!message.isFakeGroup,

    get layout(): TAlbumLayoutRow[] {
      const sizes = presenter.payloads.map(
        ({ payload }: { payload: TAlbumPayload }, idx: number) => ({
          w: payload.graphicSize.width,
          h: payload.graphicSize.height,
          id: idx,
        })
      );

      return getAlbumLayout(sizes).map((row, idx) => ({ id: idx, items: row }));
    },

    getPayload(id: number): TAlbumPayload {
      return presenter.payloads[id];
    },

    setIsFromMe(isFromMe: boolean): void {
      presenter.isFromMe = isFromMe;
    },

    setGroupId(groupId: string): void {
      presenter.groupId = groupId;
    },

    setMessageId(messageId: string): void {
      presenter.messageId = messageId;
    },

    setIsFakeGroup(isFakeGroup: boolean): void {
      presenter.isFakeGroup = isFakeGroup;
    },

    setPayloads(payloads: TChatMessagePayloads): void {
      presenter.payloads = payloads;
    },

    openMediaGallery(mediaId: string): void {
      Tasks.group.openMediaGallery({
        groupId: presenter.groupId,
        messageId: presenter.messageId,
        mediaId,
        showMessageInfo: !presenter.isFakeGroup,
      });
    },
  }));

  useEffect(() => {
    presenter.setIsFromMe(message.fromMe && !message.group?.isChannel);
    presenter.setGroupId(message.groupId);
    presenter.setMessageId(message.id);
    presenter.setIsFakeGroup(!!message.isFakeGroup);
  }, [message]);

  useEffect(() => {
    presenter.setPayloads(message.payload.payloads);
  }, [message.payload.payloads]);

  return presenter;
}
